import {
  Component,
  type ContextType,
  type ErrorInfo,
  type FunctionComponent,
  type ReactNode,
} from "react"
import { css } from "@emotion/react"
import { SentryContext } from "@heyhabito/sentry"
import {
  colours,
  breakpoints,
  typographyStyles,
  PrimaryCTA,
  vertical,
} from "design-kit"

const tryRecoverFromChunkLoadError = (
  error: Error,
  sentry: ContextType<typeof SentryContext>
): void => {
  const maxReloads = 3 // how many times should we try and reload the page?
  const countKey = "ChunkLoadError_reload_count"
  const countItem = localStorage.getItem(countKey)
  const now = new Date().getTime()
  if (countItem === null) {
    localStorage.setItem(countKey, JSON.stringify({ count: 1, timestamp: now }))
    window.location.reload()
  } else {
    const { count, timestamp } = JSON.parse(countItem)
    if (now - timestamp > 10000) {
      // If an item is older than 10 seconds then consider it stale
      localStorage.setItem(
        countKey,
        JSON.stringify({ count: 1, timestamp: now })
      )
      window.location.reload()
    } else if (count < maxReloads) {
      localStorage.setItem(
        countKey,
        JSON.stringify({ count: count + 1, timestamp })
      )
      window.location.reload()
    } else if (sentry !== null) {
      sentry.logToSentry("maxReload exceeded for ChunkLoadError", error)
    }
    // It's all gone pete tong and we can't log anything. Uh oh.
  }
}

type AnyProps = Partial<Record<string, unknown>>
type BlankState = Record<never, never>

/**
 * It might happen that the user is navigating around the application during
 * a deployment, in which case certain requests might fail due to hash changes.
 *
 * In this case all we can do is force a page refresh, which is what this error
 * boundary does.
 */
export class ChunkLoadErrorBoundary extends Component<AnyProps, BlankState> {
  static contextType = SentryContext

  static getDerivedStateFromError(): Partial<BlankState> | null {
    return {}
  }

  declare context: ContextType<typeof SentryContext>

  constructor(props: Readonly<AnyProps>) {
    super(props)
  }

  componentDidCatch(error: Error): void {
    if (error.name === "ChunkLoadError") {
      tryRecoverFromChunkLoadError(error, this.context)
    }
  }
  render(): ReactNode {
    return this.props.children
  }
}

interface PageErrorBoundaryProps {
  pageName: string
}
interface PageErrorBoundaryState {
  error: Error | null
  errorInfo: ErrorInfo | null
}
/**
 * If an error is throwing in any of the main `Page` components (./pages/*) we
 * catch it here and display a barely helpful "plz refresh" message.
 */
export class PageErrorBoundary extends Component<
  PageErrorBoundaryProps,
  PageErrorBoundaryState
> {
  static contextType = SentryContext

  static getDerivedStateFromError(
    error: Error
  ): Partial<PageErrorBoundaryState> | null {
    return { error, errorInfo: null }
  }

  declare context: ContextType<typeof SentryContext>

  constructor(props: Readonly<PageErrorBoundaryProps>) {
    super(props)
    this.state = { error: null, errorInfo: null }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ error, errorInfo })
    const { pageName } = this.props
    if (this.context) {
      this.context.logToSentryDetailed({
        description: `${pageName}: PageErrorBoundary caught an error`,
        info: {
          cause: error,
          pageName,
        },
      })
    }
  }

  render(): ReactNode {
    if (this.state.error !== null) {
      return <Oops />
    }
    return this.props.children
  }
}

/**
 * This could be a lot nicer.
 */
const Oops: FunctionComponent = () => {
  const refresh = (): void => window.location.reload()
  return (
    <div
      css={css`
        display: flex;
        height: 80%;
        align-items: center;
        justify-content: center;
        margin: ${vertical.xl} auto;
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          padding: 20px 20px 40px;

          ${breakpoints.tablet`
            max-width: 100%;
          `}
        `}
      >
        <p
          css={css`
            color: ${colours.greyScale.grey100};
            ${typographyStyles.body};
          `}
        >
          Oops, something went wrong. Try refreshing the page, or use the chat
          icon to tell us if that doesn’t work
        </p>
        <PrimaryCTA onClick={refresh} text="Refresh" />
      </div>
    </div>
  )
}
